/**
 * The sign-in screen's half of 101's phone rule.
 *
 * Supabase phone auth takes E.164, and so does `identity.identifier` for a
 * `phone_otp` row — `app.normalise_phone()` is where a BambooHR string becomes
 * one. This is where what a PERSON TYPES becomes one, so both sides have to
 * agree on the same result for the same number or 010's trigger finds no
 * identity and the person is told they are not set up.
 *
 * Deliberately smaller than the database's version: here the person picks
 * their country from a list, so there is no location string to parse. A
 * leading `+` (or `00`) means they typed the whole number and the picker is
 * ignored.
 */

export type DialCode = { country: string; label: string; dial: string }

/** Where Conetec people are. Order is the picker's order; CA first. */
export const DIAL_CODES: DialCode[] = [
  { country: 'CA', label: 'Canada / US (+1)', dial: '1' },
  { country: 'CL', label: 'Chile (+56)', dial: '56' },
  { country: 'PE', label: 'Peru (+51)', dial: '51' },
  { country: 'AU', label: 'Australia (+61)', dial: '61' },
  { country: 'GB', label: 'United Kingdom (+44)', dial: '44' },
  { country: 'FR', label: 'France (+33)', dial: '33' },
  { country: 'NZ', label: 'New Zealand (+64)', dial: '64' },
]

const E164 = /^\+[1-9]\d{7,14}$/

/** E.164, or null when what was typed cannot be one. Never a guess. */
export function toE164(typed: string, dial: string): string | null {
  const raw = typed.trim().replace(/\s*(x|ext\.?|extension)\s*\d+\s*$/i, '')
  const digits = raw.replace(/\D/g, '')
  if (!digits) return null

  let out: string
  if (raw.startsWith('+')) out = '+' + digits
  else if (digits.startsWith('00')) out = '+' + digits.slice(2)
  else if (dial === '1') {
    if (digits.length === 11 && digits.startsWith('1')) out = '+' + digits
    else if (digits.length === 10) out = '+1' + digits
    else return null
  } else if (digits.startsWith(dial) && digits.length >= dial.length + 9) {
    // Typed the country code without its '+', maybe with the trunk 0 left in.
    out = '+' + dial + digits.slice(dial.length).replace(/^0+/, '')
  } else out = '+' + dial + digits.replace(/^0+/, '')

  if (!E164.test(out)) return null
  if (out.startsWith('+1') && out.length !== 12) return null
  if (out.startsWith('+1') && !/[2-9]/.test(out[2])) return null
  return out
}

/** How a number is shown back on the code step: the last four only. */
export function maskPhone(e164: string): string {
  return e164.length > 4 ? `•••• ${e164.slice(-4)}` : e164
}
